import React, { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Inbox, Mail, Trash2, CornerUpLeft, RefreshCw, Search, Send, X, ChevronLeft, ChevronRight, Folder, Paperclip, Download } from "lucide-react";

const field = "w-full bg-noir border border-white/15 px-3 py-2.5 font-body text-sm text-cream placeholder:text-white/30 focus:border-gold focus:outline-none";
const PAGE = 30;

function extractEmail(from) {
  const m = (from || "").match(/<([^>]+)>/);
  return m ? m[1] : (from || "");
}

function formatSize(bytes) {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export default function AdminInbox() {
  const [status, setStatus] = useState(null);
  const [folders, setFolders] = useState([]);
  const [folder, setFolder] = useState("INBOX");
  const [list, setList] = useState({ total: 0, messages: [] });
  const [offset, setOffset] = useState(0);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(null);
  const [loadingMsg, setLoadingMsg] = useState(false);
  const [compose, setCompose] = useState(null); // { to, subject, body_text, in_reply_to, references, files }
  const [sending, setSending] = useState(false);
  const fileRef = useRef(null);
  const isSent = /sent/i.test(folder);

  useEffect(() => {
    api.get("/admin/mail/status").then((r) => {
      setStatus(r.data);
      if (r.data.configured) {
        api.get("/admin/mail/folders").then((f) => setFolders(f.data)).catch(() => {});
      }
    }).catch(() => setStatus({ configured: false }));
  }, []);

  const loadList = () => {
    if (!status?.configured) return;
    setLoading(true);
    setActive(null);
    api.get("/admin/mail/messages", { params: { folder, limit: PAGE, offset, search } })
      .then((r) => setList(r.data))
      .catch((e) => toast.error(e.response?.data?.detail || "Could not load mailbox"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadList(); /* eslint-disable-next-line */ }, [folder, offset, search, status?.configured]);

  const openMsg = (uid) => {
    setLoadingMsg(true);
    api.get("/admin/mail/message", { params: { folder, uid } })
      .then((r) => {
        setActive(r.data);
        setList((l) => ({ ...l, messages: l.messages.map((m) => m.uid === uid ? { ...m, seen: true } : m) }));
      })
      .catch((e) => toast.error(e.response?.data?.detail || "Could not open message"))
      .finally(() => setLoadingMsg(false));
  };

  const del = (uid) => {
    api.post("/admin/mail/delete", { folder, uid })
      .then(() => { toast.success("Deleted"); setActive(null); loadList(); })
      .catch(() => toast.error("Could not delete"));
  };

  const startReply = () => {
    if (!active) return;
    setCompose({
      to: extractEmail(active.from),
      subject: active.subject?.toLowerCase().startsWith("re:") ? active.subject : `Re: ${active.subject}`,
      body_text: `\n\n---------- On ${active.date ? new Date(active.date).toLocaleString() : ""}, ${active.from} wrote ----------\n${(active.text || "").split("\n").map((l) => "> " + l).join("\n")}`,
      in_reply_to: active.message_id,
      references: active.message_id,
      files: [],
    });
  };

  const startCompose = () => setCompose({ to: "", subject: "", body_text: "", in_reply_to: null, references: null, files: [] });

  const onPickFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    const limit = 15 * 1024 * 1024;
    const ok = [];
    for (const f of picked) {
      if (f.size > limit) { toast.error(`${f.name} exceeds 15MB`); continue; }
      ok.push(f);
    }
    setCompose((c) => ({ ...c, files: [...(c.files || []), ...ok] }));
    if (fileRef.current) fileRef.current.value = "";
  };

  const removeFile = (idx) => setCompose((c) => ({ ...c, files: c.files.filter((_, i) => i !== idx) }));

  const downloadAttachment = async (att) => {
    try {
      const r = await api.get("/admin/mail/attachment", {
        params: { folder, uid: active.uid, index: att.index }, responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = att.filename || "attachment";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download attachment"); }
  };

  const sendMail = async () => {
    if (!compose.to || !compose.subject) return toast.error("To and subject are required");
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("to", compose.to);
      fd.append("subject", compose.subject);
      fd.append("body_text", compose.body_text || "");
      fd.append("body_html", `<div style="font-family:Georgia,serif;white-space:pre-wrap;">${(compose.body_text || "").replace(/</g, "&lt;")}</div>`);
      if (compose.in_reply_to) fd.append("in_reply_to", compose.in_reply_to);
      if (compose.references) fd.append("references", compose.references);
      (compose.files || []).forEach((f) => fd.append("files", f));
      await api.post("/admin/mail/send", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Sent");
      setCompose(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally { setSending(false); }
  };

  if (status && !status.configured) {
    return (
      <div className="max-w-xl" data-testid="inbox-not-configured">
        <h2 className="font-serif text-2xl text-cream mb-4">Inbox</h2>
        <div className="bg-noir-2 border border-white/10 p-7 font-body text-sm text-white/60 space-y-3">
          <p className="text-cream flex items-center gap-2"><Inbox className="w-4 h-4 text-gold" /> Mailbox not connected yet.</p>
          <p>To enable the {status?.email || "info@afterdarkchoc.com"} inbox, add your IONOS mailbox password to the backend environment:</p>
          <pre className="bg-noir border border-white/10 p-3 text-xs text-champagne overflow-x-auto">MAILBOX_EMAIL="info@afterdarkchoc.com"
MAILBOX_PASSWORD="your-ionos-password"</pre>
          <p className="text-white/45 text-xs">IMAP {status?.imap_host}:993 · SMTP {status?.smtp_host}:465. Once set, this page shows your live inbox.</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="inbox-panel">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <h2 className="font-serif text-2xl text-cream">Inbox <span className="text-white/40 text-base">· {status?.email}</span></h2>
        <div className="flex items-center gap-2">
          <button onClick={startCompose} data-testid="compose-btn" className="inline-flex items-center gap-1.5 bg-gold text-black font-body uppercase tracking-[0.15em] text-[10px] px-3 py-2 hover:bg-champagne transition-colors"><Mail className="w-3 h-3" /> Compose</button>
          <button onClick={loadList} data-testid="refresh-btn" className="inline-flex items-center gap-1.5 border border-white/20 text-white/70 font-body uppercase tracking-[0.15em] text-[10px] px-3 py-2 hover:border-gold hover:text-gold transition-colors"><RefreshCw className="w-3 h-3" /> Refresh</button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[180px_320px_1fr] gap-4 min-h-[520px]">
        {/* Folders */}
        <div className="border border-white/10 p-2 h-fit" data-testid="inbox-folders">
          {folders.map((f) => (
            <button key={f.name} onClick={() => { setFolder(f.name); setOffset(0); }}
              className={`w-full text-left px-3 py-2 font-body text-xs flex items-center gap-2 ${folder === f.name ? "bg-gold/10 text-gold" : "text-white/60 hover:text-cream"}`}>
              <Folder className="w-3.5 h-3.5" /> {f.name}
            </button>
          ))}
          {folders.length === 0 && <p className="p-3 font-body text-xs text-white/40">No folders</p>}
        </div>

        {/* Message list */}
        <div className="border border-white/10 flex flex-col">
          <form onSubmit={(e) => { e.preventDefault(); setOffset(0); setSearch(searchInput); }} className="p-2 border-b border-white/10 flex gap-2">
            <div className="relative flex-1">
              <Search className="w-3.5 h-3.5 text-white/30 absolute left-2 top-1/2 -translate-y-1/2" />
              <input value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Search" data-testid="inbox-search"
                className="w-full bg-noir border border-white/15 pl-7 pr-2 py-1.5 font-body text-xs text-cream focus:border-gold focus:outline-none" />
            </div>
          </form>
          <div className="flex-1 overflow-y-auto divide-y divide-white/5" data-testid="inbox-list">
            {loading ? <p className="p-4 font-body text-xs text-white/40">Loading…</p>
              : list.messages.length === 0 ? <p className="p-4 font-body text-xs text-white/40">No messages</p>
              : list.messages.map((m) => (
                <button key={m.uid} onClick={() => openMsg(m.uid)} data-testid={`inbox-msg-${m.uid}`}
                  className={`w-full text-left p-3 hover:bg-white/5 transition-colors ${active?.uid === m.uid ? "bg-white/5" : ""}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className={`font-body text-xs truncate ${m.seen ? "text-white/60" : "text-cream font-semibold"}`}>{isSent ? `To: ${m.to || "—"}` : m.from_name}</span>
                    <span className="font-body text-[10px] text-white/30 whitespace-nowrap flex items-center gap-1">{m.has_attachments && <Paperclip className="w-3 h-3 text-champagne" />}{m.date ? new Date(m.date).toLocaleDateString() : ""}</span>
                  </div>
                  <p className={`font-body text-xs truncate mt-0.5 ${m.seen ? "text-white/50" : "text-champagne"}`}>{m.subject}</p>
                  <p className="font-body text-[11px] text-white/35 truncate mt-0.5">{m.snippet}</p>
                </button>
              ))}
          </div>
          <div className="p-2 border-t border-white/10 flex items-center justify-between font-body text-[11px] text-white/40">
            <span>{list.total} total</span>
            <div className="flex gap-2">
              <button disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))} className="disabled:opacity-30"><ChevronLeft className="w-4 h-4" /></button>
              <button disabled={offset + PAGE >= list.total} onClick={() => setOffset(offset + PAGE)} className="disabled:opacity-30"><ChevronRight className="w-4 h-4" /></button>
            </div>
          </div>
        </div>

        {/* Reader */}
        <div className="border border-white/10 p-5 overflow-y-auto" data-testid="inbox-reader">
          {loadingMsg ? <p className="font-body text-sm text-white/40">Opening…</p>
            : !active ? <p className="font-body text-sm text-white/40">Select a message to read.</p>
            : (
              <div>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="font-serif text-xl text-cream">{active.subject}</h3>
                    <p className="font-body text-xs text-white/50 mt-1">From: {active.from}</p>
                    <p className="font-body text-xs text-white/40">To: {active.to}</p>
                    <p className="font-body text-[11px] text-white/30">{active.date ? new Date(active.date).toLocaleString() : ""}</p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button onClick={startReply} data-testid="reply-btn" title="Reply" className="border border-gold/50 text-gold p-2 hover:bg-gold hover:text-black transition-colors"><CornerUpLeft className="w-4 h-4" /></button>
                    <button onClick={() => del(active.uid)} data-testid="delete-msg-btn" title="Delete" className="border border-white/20 text-white/60 p-2 hover:border-red-400 hover:text-red-400 transition-colors"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
                {active.attachments?.filter((a) => !a.inline).length > 0 && (
                  <div className="mt-4 flex flex-wrap gap-2" data-testid="attachment-list">
                    {active.attachments.filter((a) => !a.inline).map((a) => (
                      <button key={a.index} onClick={() => downloadAttachment(a)} data-testid={`attachment-${a.index}`}
                        className="inline-flex items-center gap-2 border border-white/15 bg-noir px-3 py-2 hover:border-gold transition-colors group">
                        <Paperclip className="w-3.5 h-3.5 text-champagne" />
                        <span className="font-body text-xs text-cream max-w-[180px] truncate">{a.filename}</span>
                        {a.size ? <span className="font-body text-[10px] text-white/35">{formatSize(a.size)}</span> : null}
                        <Download className="w-3.5 h-3.5 text-white/40 group-hover:text-gold" />
                      </button>
                    ))}
                  </div>
                )}
                <div className="mt-4 border-t border-white/10 pt-4">
                  {active.html
                    ? <div className="mail-html font-body text-sm text-white/75 leading-relaxed" dangerouslySetInnerHTML={{ __html: active.html }} />
                    : <pre className="font-body text-sm text-white/75 whitespace-pre-wrap leading-relaxed">{active.text}</pre>}
                </div>
              </div>
            )}
        </div>
      </div>

      {/* Compose / Reply */}
      {compose && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="compose-modal">
          <div className="bg-noir-2 border border-white/10 w-full max-w-lg">
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
              <h3 className="font-serif text-xl text-cream">{compose.in_reply_to ? "Reply" : "New Message"}</h3>
              <button onClick={() => setCompose(null)}><X className="w-5 h-5 text-white/50 hover:text-gold" /></button>
            </div>
            <div className="p-5 space-y-3">
              <input value={compose.to} onChange={(e) => setCompose({ ...compose, to: e.target.value })} placeholder="To" data-testid="compose-to" className={field} />
              <input value={compose.subject} onChange={(e) => setCompose({ ...compose, subject: e.target.value })} placeholder="Subject" data-testid="compose-subject" className={field} />
              <textarea rows={10} value={compose.body_text} onChange={(e) => setCompose({ ...compose, body_text: e.target.value })} placeholder="Write your message…" data-testid="compose-body" className={field} />
              {compose.files?.length > 0 && (
                <div className="flex flex-wrap gap-2" data-testid="compose-attachments">
                  {compose.files.map((f, i) => (
                    <span key={i} className="inline-flex items-center gap-2 border border-white/15 bg-noir px-2.5 py-1.5 font-body text-[11px] text-cream">
                      <Paperclip className="w-3 h-3 text-champagne" />
                      <span className="max-w-[160px] truncate">{f.name}</span>
                      <span className="text-white/35">{formatSize(f.size)}</span>
                      <button type="button" onClick={() => removeFile(i)} data-testid={`remove-attachment-${i}`}><X className="w-3 h-3 text-white/40 hover:text-red-400" /></button>
                    </span>
                  ))}
                </div>
              )}
              <div className="flex items-center justify-between gap-3">
                <input ref={fileRef} type="file" multiple onChange={onPickFiles} className="hidden" data-testid="compose-file-input" />
                <button type="button" onClick={() => fileRef.current?.click()} data-testid="compose-attach-btn"
                  className="inline-flex items-center gap-1.5 border border-white/20 text-white/70 font-body uppercase tracking-[0.15em] text-[10px] px-3 py-2 hover:border-gold hover:text-gold transition-colors">
                  <Paperclip className="w-3 h-3" /> Attach files
                </button>
                <button onClick={sendMail} disabled={sending} data-testid="compose-send" className="flex-1 inline-flex items-center justify-center gap-2 py-3 bg-gold text-black font-body uppercase tracking-[0.2em] text-xs hover:bg-champagne transition-colors disabled:opacity-50">
                  <Send className="w-4 h-4" /> {sending ? "Sending…" : "Send"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
